import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Mail, Phone, Save, UserRound } from 'lucide-react';
import EducationSection from './EducationSection';
import ProjectsSection from './ProjectsSection';
import SkillsInput from './SkillsInput';
import { FieldError, SectionHeader, inputClass, profileCard } from './ProfileUI';

const initialProfile = {
  name: '',
  email: '',
  phone: '',
  education: [],
  skills: [],
  projects: [],
};

const ProfileForm = ({ initialValue, onSubmit, isSaving, reviewMessage }) => {
  const [profile, setProfile] = useState(initialValue || initialProfile);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setProfile(initialValue || initialProfile);
    setErrors({});
  }, [initialValue]);

  const updateField = (field, value) => {
    setProfile((current) => ({ ...current, [field]: value }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!profile.name.trim()) nextErrors.name = 'Name is required';
    if (!profile.email.trim()) nextErrors.email = 'Email is required';
    if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) nextErrors.email = 'Enter a valid email';
    if (!profile.skills.length) nextErrors.skills = 'Add at least one skill';
    nextErrors.education = profile.education.map((entry) => (!entry.institution.trim() || !entry.degree.trim() ? 'Institution and degree are required' : ''));
    nextErrors.projects = profile.projects.map((project) => (!project.title.trim() || !project.description.trim() ? 'Project title and description are required' : ''));

    const hasErrors = Object.entries(nextErrors).some(([key, value]) => {
      if (key === 'education' || key === 'projects') return value.some(Boolean);
      return Boolean(value);
    });
    setErrors(nextErrors);
    return !hasErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;
    onSubmit({
      ...profile,
      phone: profile.phone || null,
      skills: profile.skills.map((skill) => ({ name: skill.name.trim() })),
      education: profile.education.map((entry) => ({
        institution: entry.institution.trim(),
        degree: entry.degree.trim(),
        field_of_study: entry.field_of_study?.trim() || null,
        start_year: entry.start_year?.trim() || null,
        end_year: entry.end_year?.trim() || null,
      })),
      projects: profile.projects.map((project) => ({
        title: project.title.trim(),
        description: project.description.trim(),
      })),
    });
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-5"
    >
      {reviewMessage && (
        <div className="rounded-2xl border border-purple-400/30 bg-purple-500/15 px-4 py-3 text-sm font-medium leading-6 text-purple-100">
          {reviewMessage}
        </div>
      )}
      <section className={profileCard}>
        <SectionHeader icon={UserRound} eyebrow="Step 01" title="Personal details" />
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-slate-200">Name</span>
            <div className="relative">
              <UserRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input className={`${inputClass} pl-10`} value={profile.name} onChange={(event) => updateField('name', event.target.value)} placeholder="Your full name" />
            </div>
            <FieldError>{errors.name}</FieldError>
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-slate-200">Email</span>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input className={`${inputClass} pl-10`} value={profile.email} onChange={(event) => updateField('email', event.target.value)} placeholder="you@example.com" />
            </div>
            <FieldError>{errors.email}</FieldError>
          </label>
          <label className="block md:col-span-2">
            <span className="mb-2 block text-sm font-semibold text-slate-200">Phone</span>
            <div className="relative">
              <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input className={`${inputClass} pl-10`} value={profile.phone || ''} onChange={(event) => updateField('phone', event.target.value)} placeholder="Optional contact number" />
            </div>
          </label>
        </div>
      </section>
      <SkillsInput skills={profile.skills} onChange={(skills) => updateField('skills', skills)} error={errors.skills} />
      <EducationSection education={profile.education} onChange={(education) => updateField('education', education)} errors={errors.education} />
      <ProjectsSection projects={profile.projects} onChange={(projects) => updateField('projects', projects)} errors={errors.projects} />
      <button
        type="submit"
        disabled={isSaving}
        className="group flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 px-5 py-4 font-bold text-white shadow-2xl shadow-purple-500/20 transition hover:-translate-y-0.5 hover:from-purple-700 hover:to-pink-700 hover:shadow-pink-500/25 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5 transition group-hover:scale-110" />}
        {isSaving ? 'Saving profile...' : 'Save Profile'}
      </button>
    </motion.form>
  );
};

export default ProfileForm;
